import { Get, HvHostMap, Put } from './helpers';
import { store } from '@/store/main';
import { MuxPlaybackIdRetrieveResponse, ChannelMuxVocabulary, MuxStreamData } from '@/types/mux.types';

export function GetMuxStreamData(session_uuid: string, language: string): Promise<MuxStreamData> {
  return Get({
    path: `/v4/mux/metrics/${session_uuid}/${language}`,
  });
}

interface GetMuxJwtProps {
  id: string;
  userId: string;
  userToken: string;
  // asAdmin?: boolean,
  thumbnail?: boolean;
  drm?: boolean;
  thumbParams?: {
    time: number;
    width?: number;
    height?: number;
    fit_mode?: string;
  };
}
export function GetMuxJwt({
  id,
  userId,
  userToken,
  // asAdmin = false,
  thumbnail = false,
  drm = false,
  thumbParams,
}: GetMuxJwtProps): Promise<{ token: string; drmToken?: string }> {
  // this just exists to bust the cache on the route
  const params = new URLSearchParams({ u: userId });

  if (thumbnail) {
    params.append('thumbnail', 'true');

    if (thumbParams) {
      params.append('thumb_params', JSON.stringify(thumbParams));
    }
  }

  if (drm) {
    params.append('drm', 'true');
  }

  // request uses browser cache so we don't need to go overboard trying to store this
  // just cache on the user's uuid in case someone signs out
  return Get({
    url: `${HvHostMap.sessionTracking}/mux-jwt/${id}?${params.toString()}`,
    headers: {
      'Content-Type': 'application/json',
      'Bl-Token': userToken,
      'bl-global-resource': 'false',
    },
  });
}

// we don't necessarily know at this point whether the playback urls require signatures or not
// until we can refactor this. For now, we'll just check if the playback url is HLS, if so we'll check
// if a signature is required, if yes we'll generate one, test it, then return it. Otherwise we'll just return the url.
export const getPlayableUrlWithToken = async (uri: string, drmEnabled?: boolean) => {
  try {
    // this is not an HLS video, just return the url
    if (!uri.includes('.m3u8')) {
      return {
        url: uri,
        drmToken: undefined,
        playbackId: undefined,
        drmRequired: false,
        ok: true,
      };
    }

    // this is not a mux video, just return the url
    if (!(uri.includes('mux.com') || uri.includes('videodelivery.brandlive'))) {
      return {
        url: uri,
        drmToken: undefined,
        playbackId: undefined,
        drmRequired: false,
        ok: true,
      };
    }

    const state = store.getState();
    const user = state.user.user;
    const userToken = state.user.idToken;

    // DRM-enabled channels cannot use the custom mux playback domain
    const url = new URL(uri);
    if (drmEnabled) {
      url.hostname = 'stream.mux.com';
    }

    // check if the playback URL is valid
    // if a token is required this will return a 403
    const response = await fetch(url.href, { method: 'HEAD' });
    const id = url.pathname.replace(/^\/|\.m3u8/g, '');

    // we attempted to load the URL with no signature but it failed with a 403
    if (response.status === 403 && userToken) {
      const userId = user?.email || '';

      // get a header token...
      const tokens = await GetMuxJwt({ id, userId, userToken });

      url.searchParams.set('token', tokens.token);
      const signedUrl = url.href;

      // confirm that the signed URL works
      const retry = await fetch(signedUrl, { method: 'HEAD' });

      // respond with the signed URL if it works and whether or not DRM is required
      if (retry.ok) {
        return {
          url: url.href,
          drmToken: tokens.drmToken,
          playbackId: id,
          drmRequired: !!tokens.drmToken,
          ok: true,
        };
      }
    }

    // initial response was not Forbidden - no signature required
    return {
      url: url.href,
      drmToken: undefined,
      playbackId: id,
      drmRequired: false,
      ok: response.ok,
    };
  } catch (e) {
    console.error(e);
    return { url: uri, drmToken: undefined, playbackId: undefined, drmRequired: false };
  }
};

// we don't necessarily know at this point whether the playback urls require signatures or not
// until we can refactor this. For now, we'll just check if the playback url is HLS, if so we'll check
// if a signature is required, if yes we'll generate one, test it, then return it. Otherwise we'll just return the url.
export const getThumbnailUrlWithToken = async (uri: string) => {
  try {
    if (!uri.includes('.webp')) {
      return uri;
    }

    if (!(uri.includes('mux.com') || uri.includes('videodelivery.brandlive'))) {
      return uri;
    }

    const url = new URL(uri);
    const state = store.getState();
    const user = state.user.user;
    const userToken = state.user.idToken;
    const response = await fetch(url, { method: 'HEAD' });

    // the request was forbidden - this happens when the thumbnail is private/DRM-enabled
    if (response.status === 403 && userToken) {
      // DRM-enabled channels cannot use the custom mux playback domain, so
      // fall back to the default mux domain
      url.hostname = 'image.mux.com';
      const id = new URL(url).pathname.split('/')[1];
      const userId = user?.email || '';
      const headers = await GetMuxJwt({ id, userId, userToken, thumbnail: true });
      const params = new URLSearchParams({ token: headers.token });
      const signedUrl = `${url}?${params.toString()}`;
      const retry = await fetch(signedUrl);
      if (retry.ok) {
        return signedUrl;
      } else {
        return new Error(`Failed to fetch thumbnail with signed URL: ${retry.status} ${retry.statusText}`);
      }
    }

    return uri;
  } catch (e) {
    console.error(e);
    return uri;
  }
};

export function UpdateChannelMuxVocabulary(
  channel: number,
  muxVocabulary: ChannelMuxVocabulary
): Promise<ChannelMuxVocabulary> {
  return Put({
    path: `/v4/mux/${channel}/vocabulary`,
    body: muxVocabulary,
  });
}

export async function GetMuxPlaybackIdDetails(
  muxPlaybackId: string,
  asAdmin: boolean,
  userToken: string
): Promise<MuxPlaybackIdRetrieveResponse | null> {
  try {
    const resp = await fetch(`${HvHostMap.sessionTracking}/playback-policy/${muxPlaybackId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        [asAdmin ? 'Bl-Token' : 'Bl-Profile-Token']: userToken,
      },
      cache: 'force-cache',
    });
    if (!resp.ok || resp.status !== 200) {
      return null;
    }
    const json = await resp.json();
    return json;
  } catch (error) {
    console.error(error);
    return null;
  }
}
